import React, { memo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Icon, Tooltip } from '~/components';
import type { ArtistDetails } from '~/modules/artist-roster/types';
import { formatCount } from '~/modules/artist-roster/utils/formatCount';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

interface SocialLinksProps {
  artistData?: ArtistDetails;
}

/**
 * This component is wrapped in React.memo() to prevent unnecessary re-renders with
 * the ScrollCollapse logic. See https://github.com/wmgtech/wmg2-one-app/pull/1747
 */
export const SocialLinks: React.FC<SocialLinksProps> = memo(
  ({ artistData }) => {
    const { t } = useTranslation('roster');
    const { t: nameTranslation } = useTranslation('names');
    const { trackEvent } = useAnalytics('Artist Roster');

    if (!artistData) {
      return null;
    }

    const handleSocialIconButtonClick = () => {
      trackEvent('artistHeroInteraction', {
        artist_id: artistData.partyId,
        artist_name: artistData.name,
      });
    };

    return (
      <div className="flex items-center justify-center gap-md">
        {artistData.socials.map((social) => {
          return (
            <div
              key={`${social.name}_${social.url}`}
              className="flex items-center gap-xs rounded-full"
            >
              <Tooltip
                content={
                  <span>
                    <Trans
                      t={t}
                      i18nKey={
                        social.name === 'youtube'
                          ? 'labels.subscriberFrom'
                          : 'labels.followerFrom'
                      }
                      values={{
                        number: social.followerCount
                          ? social.followerCount.toLocaleString()
                          : 0,
                        social: nameTranslation(social.name),
                      }}
                      components={{
                        strong: <span className="font-semibold" />,
                      }}
                    />
                  </span>
                }
                delayDuration={0}
                side="bottom"
                sideOffset={4}
                align="start"
                triggerAsChild
                size="md"
              >
                <Link
                  to={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.name}
                  onClick={handleSocialIconButtonClick}
                  className="flex items-center gap-sm rounded-full px-md py-sm transition duration-300 hover:bg-emphasis-strong"
                >
                  <Icon variant="brands" type={social.name} />
                  <span>{formatCount(social.followerCount)}</span>
                </Link>
              </Tooltip>
            </div>
          );
        })}
      </div>
    );
  },
);

SocialLinks.displayName = 'SocialLinks';
